import { registerBrowserRoutes } from '@zg-rentals/route-list';
import type { PathsManifest } from '@zg-rentals/route-list';
import type { Brand } from '@zg-rentals/multibrand-support';

function pathsManifest(baseUrl: string, brand: Brand) {
  function formatUrl(url = '') {
    if (url) {
      return `${baseUrl}/${url}`;
    } else {
      return `${baseUrl}`;
    }
  }

  function concatParams(queryString: string | null = null, url: string, encode = false) {
    if (!queryString) {
      return url.split('?')[0];
    }

    if (encode) {
      return url + encodeURIComponent(queryString);
    } else {
      return url + queryString;
    }
  }

  /* tslint:disable:max-line-length */
  return {
    // pages
    base: formatUrl(),
    conversationsPage: formatUrl(),
    devToolsPage: formatUrl('devtools'),

    // ZRM Pages
    homePage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return 'https://hotpads.com/rental-manager';
        case 'zillow':
          return 'https://zillow.com/rental-manager';
        default:
          break;
      }
    })(brand),

    // ZRM Sub-routes
    propertyDetailsContacts: 'contacts',
    propertyDetailsListing: 'listing',

    //hotpads
    adminPage: '/admin',
    experimentPage: 'https://hotpads.com/node/rep/abtests',
    repsPage: '/rep',
    salesPage: '/sales',
    //end hotpads

    //zillow
    fsboPage: '/for-sale-by-owner',
    multifamilyAdvertisingPage: 'https://www.zillow.com/marketing/rental-property-advertising',
    resourcesPage: '/rental-manager/resources/',
    mfhResourcesPage: '/multifamily-knowledge-center/',
    tenantScreeningPage: 'https://www.zillow.com/marketing/rental-manager/tenant-screening/',
    zestimatePage: '/info/what-is-a-rent-zestimate/',
    impersonatePage: 'https://www.zillow.com/user/Impersonate.htm',
    zillowLogoutPage: '/Logout.htm',
    corpTermsURL: 'https://www.zillow.com/corp/Terms.htm',
    rentalTermsURL: 'https://www.zillow.com/renter-hub/terms/Rental-User-Terms',
    pledgeURL: 'https://www.zillow.com/corp/respectful-renting-pledge/',
    photosFormat: 'https://www.zillow.help/article/do-the-photos-need-to-be-a-certain-format-and-size-zd30234452011667',
    //end zillow

    // Export Excel Paths
    exportExcel: {
      leads: '/users/leads/exportExcel',
      adSpendAll: '/users/analytics/adproducts/exportExcel',
      boostAd: '/users/analytics/cpmBoostStats/exportExcel',
      properties: '/users/analytics/properties/exportExcel',
    },

    stopImpersonatingPage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return 'https://hotpads.com/rep/changeUserId.htm?action=changeUserIdBackToRep';
        case 'zillow':
          return 'https://www.zillow.com/myzillow/Profile,$PageWrapper.$DirectLink.sdirect';
        default:
          break;
      }
    })(brand),

    myPropertiesPage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return 'https://hotpads.com/rental-manager/properties';
        case 'zillow':
          return 'https://www.zillow.com/rental-manager/properties';
        default:
          break;
      }
    })(brand),

    globalContactsPage: 'https://www.zillow.com/rental-manager/contacts',

    // RMW Mobile Apps
    iOS: 'https://itunes.apple.com/us/app/postlets-post-rentals-free/id759202892?ls=1&mt=8',
    android: 'https://play.google.com/store/apps/details?id=com.zillow.postlet',

    hud: 'http://www.hud.gov/offices/fheo/index.cfm',

    profilePage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return '/user/edit';
        case 'zillow':
          return '/user/EditProfile.htm';
        default:
          break;
      }
    })(brand),

    signInPage: (brand: Brand, queryString: string) => {
      switch (brand) {
        case 'hotpads':
          return concatParams(queryString, '/login?redirect=');
        case 'zillow':
          return concatParams(queryString, '/user/acct/login/?url=', true);
        default:
          break;
      }
    },

    helpPage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return 'https://help.hotpads.com/hc/en-us';
        case 'zillow':
          return 'https://www.zillow.help/renter-landlord-property-manager';
        default:
          break;
      }
    })(brand),

    userSettingsPage: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return '/user/settings';
        case 'zillow':
          return '/rental-manager/settings';
        default:
          break;
      }
    })(brand),

    logout: ((brand: Brand) => {
      switch (brand) {
        case 'hotpads':
          return '/';
        case 'zillow':
          return '/rental-manager';
        default:
          break;
      }
    })(brand),

    registerPage: (brand: Brand, queryString: string) => {
      switch (brand) {
        case 'zillow':
          return concatParams(queryString, '/user/acct/register/?url=', true);
        default:
          break;
      }
    },

    marketingRoutes: {
      onboarding: {
        applications: formatUrl('properties/applications'),
        payments: formatUrl('properties/payments'),
      },
      screening: formatUrl('tenant-screening'),
      collectRent: formatUrl('collect-rent-online'),
      screeningLangingPage: 'https://www.zillow.com/marketing/landlord/tenant-screening/',
      collectRentLandingPage: 'https://www.zillow.com/marketing/rental-manager/collect-rent-online/',
    },
  };
}

registerBrowserRoutes(pathsManifest('/renter-hub/inbox', 'hotpads') as PathsManifest);

export default pathsManifest;
